import { Body, Button, Heading } from '@assembly-js/design-system';
import { Popper } from '@mui/material';
import type { MenuProps, PopperPlacementType } from '@mui/material';
import clsx from 'clsx';
import React from 'react';

const POPPER_OFFSET = 8;
const DEFAULT_WIDTH = 320;

export const COACHMARK_HIGHLIGHT_CLASS = 'bg-[rgba(180,191,228,0.23)]';

// Render a coachmark tour menu as a non-modal popover: let clicks fall through its
// root to the page (so the backdrop no longer catches click-away and the menu
// stays open) while keeping the paper interactive, and drop the modal's scroll
// lock, focus trap, and Escape-to-close so the rest of the page stays usable.
export const COACHMARK_MENU_PROPS: Partial<MenuProps> = {
  disableScrollLock: true,
  disableEnforceFocus: true,
  disableEscapeKeyDown: true,
  slotProps: {
    root: { style: { pointerEvents: 'none' } },
    paper: { style: { pointerEvents: 'auto' } },
  },
};

/** A real element or a Popper virtual element (rect-only) anchor. */
export type CoachmarkAnchor =
  | HTMLElement
  | { getBoundingClientRect: () => DOMRect }
  | null;

/**
 * For top/bottom cards: which horizontal edge the arrow sits on.
 * For left/right cards: which vertical edge (`start` = top, `end` = bottom).
 */
export type CoachmarkArrowAlignment = 'start' | 'end';

// A rotated square straddling the card edge (protruding toward the anchor); the
// two outward faces carry the border so the tip continues the card outline. The
// edge offset is half the 12px square (6px) PLUS the 1px card border = 7px, which
// lands the square's centre — where its two bordered faces meet — exactly on the
// card's outer border edge. A plain half-square offset sits 1px inside, leaving
// the card border drawn across the arrow's base so the tip looks detached.
// For a left/right card the arrow's `start` offset lands on the vertical center
// of a single standard-height menu row that the card's top edge is aligned to.
const getArrowClassName = (
  placement: PopperPlacementType,
  arrowAlignment?: CoachmarkArrowAlignment,
): string => {
  const base =
    'absolute h-3 w-3 rotate-45 border-0 border-solid border-non-hover-border bg-white';
  const alignEnd = arrowAlignment
    ? arrowAlignment === 'end'
    : placement.endsWith('-end');
  const horizontal = alignEnd ? 'right-6' : 'left-6';
  if (placement.startsWith('bottom')) {
    return `${base} top-[-7px] ${horizontal} border-l border-t`;
  }
  if (placement.startsWith('top')) {
    return `${base} bottom-[-7px] ${horizontal} border-b border-r`;
  }
  const vertical =
    arrowAlignment === 'start'
      ? 'top-[14px]'
      : arrowAlignment === 'end'
        ? 'bottom-6'
        : 'top-1/2 -translate-y-1/2';
  if (placement.startsWith('left')) {
    return `${base} right-[-7px] ${vertical} border-r border-t`;
  }
  return `${base} left-[-7px] ${vertical} border-b border-l`;
};

export interface CoachmarkProps {
  open: boolean;
  anchorEl: CoachmarkAnchor;
  placement: PopperPlacementType;
  title: string;
  description: string;
  stepLabel: string;
  primaryLabel?: string;
  onPrimary?: () => void;

  lockPlacement?: boolean;

  /** Overrides the placement-derived arrow side/edge. */
  arrowAlignment?: CoachmarkArrowAlignment;

  offset?: number;

  /**
   * Inline-style overrides for the card — typically `width` and/or `maxWidth`.
   * When provided, the card switches to border-box so these values bound the
   * rendered card (padding and border included) rather than just its content.
   * Used e.g. on narrow layouts to cap the width and keep a gap at the edge.
   */
  style?: React.CSSProperties;

  /** Tighter padding and smaller type, for narrow cards on phones, on the menu's layer. */
  compact?: boolean;
}

export const Coachmark: React.FC<CoachmarkProps> = ({
  open,
  anchorEl,
  placement,
  title,
  description,
  stepLabel,
  primaryLabel,
  onPrimary,
  lockPlacement = false,
  arrowAlignment,
  offset = POPPER_OFFSET,
  style,
  compact = false,
}) => {
  if (!open || !anchorEl) {
    return null;
  }

  const modifiers = [
    { name: 'offset', options: { offset: [0, offset] } },
    ...(lockPlacement
      ? []
      : [
          { name: 'preventOverflow', options: { padding: 8 } },
          { name: 'flip', options: { padding: 8 } },
        ]),
  ];

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement={placement}
      // Sit just below the modal layer (1300) so a real dialog and its backdrop
      // cover the coachmark. The tour's menu step is non-modal (no backdrop) and
      // its card is placed beside — not over — the menu, so it stays visible there.
      // A compact card hangs under the menu with its arrow on the menu's edge, so
      // it shares the menu's layer and, mounting after it, paints over it; a
      // dialog opened later still mounts after the card and covers it.
      className={compact ? 'z-modal' : 'z-1299'}
      modifiers={modifiers}
    >
      {({ placement: activePlacement }) => (
        <div
          role="dialog"
          aria-label={title}
          className={clsx(
            'pointer-events-auto relative rounded-lg border border-solid border-non-hover-border bg-white shadow-[0_6px_20px_rgba(0,0,0,0.07)]',
            compact ? 'p-3' : 'p-4',
          )}
          style={{
            width: DEFAULT_WIDTH,
            ...(style ? { boxSizing: 'border-box', ...style } : {}),
          }}
        >
          <span
            className={getArrowClassName(activePlacement, arrowAlignment)}
          />
          <Heading
            tag="h3"
            className={clsx(
              'text-black-heading',
              compact && 'text-[14px]! font-normal! leading-5!',
            )}
          >
            {title}
          </Heading>
          <Body
            tag="div"
            size="sm"
            className={clsx(
              'mt-1 text-gray-small',
              compact && 'text-[12px]! leading-[18px]!',
            )}
          >
            {description}
          </Body>
          <div
            className={clsx(
              'flex items-center justify-between',
              compact ? 'mt-2.5' : 'mt-3',
            )}
          >
            <Body tag="span" size="xs" className="text-gray-small">
              {stepLabel}
            </Body>
            {primaryLabel && onPrimary && (
              <Button
                variant="secondary"
                size="sm"
                label={primaryLabel}
                onClick={onPrimary}
              />
            )}
          </div>
        </div>
      )}
    </Popper>
  );
};
